import React, { createContext, useContext, useState, useEffect } from 'react'
import { serverOwnsTitleFor } from './utils/head'
import { withinCommunity } from './utils/title'
import { useTranslation } from 'react-i18next'
import { sdk } from './sdk'
import { useMenu } from './MenuContext'
import { useCommunity } from './CommunityContext'
import { trackPageView } from './hooks/useGoogleAnalytics'
import { collectPageView } from './analytics/collector'
import { isPricingPath } from './checkout/pricingPath'
import { isOfferPath } from './downsellPath'
import { isPaymentPath } from './checkout/pendingCheckout'
import { isProductPath, isWaitlistPath } from './products/paths'
import { isVerifyPath } from './verifyPath'
import { isInviteDeclinePath } from './inviteDeclinePath'
import { isEmailUnsubscribePath } from './emailUnsubscribePath'
import { isAccountErasureCancelPath } from './erasure'
import { privacyExportIdOf } from './personalDataExport'
import { isSurveyPath } from './surveyPaths'
import { isPublicFormPath } from './formPaths'
import { interceptNavigation, NavigationGuardPrompt } from './navigationGuard'
import { isSupportTicketPath } from './supportPath'
import { isBookingNewPath } from './roomPanelPath'
import { isAdminCommunitiesPath } from './adminCommunitiesPath'
import { isAdminCommunityRequestsPath } from './adminCommunityRequestsPath'
import { ORGANIZING_EVENTS_PATH, eventCheckInIdOf } from './eventKioskPath'
import { canonicalPath, canonicalizeLocation } from './legacyPaths'
import { syncCanonical } from './utils/head'
import { internalLinkTarget, scrollToHash } from './internalLinkClicks'
import { isSettingsPath } from '@/components/features/settings/settingsSections'
import { ROUTES } from './routes'
import type { Space } from '@/types/spaces'
import { denialOf, type AccessDenial } from '@/types/access'

/**
 * A space the server refused to open (403), as the menu described it.
 *
 * Only the 403 lands here. A network failure or a 404 keeps today's fallback
 * to the home space: neither one says anything about access, and a screen
 * saying "this is exclusive" for a timeout would be a lie.
 */
export interface DeniedSpace {
  /** The route that was refused; the state means nothing on any other one. */
  path: string
  /** The space as the menu listed it: `title`, `icon`, `locked`, `lockReason`. */
  space: Pick<Space, 'id' | 'title' | 'icon' | 'description' | 'locked' | 'lockReason'>
  section: { slug: string; title?: string }
  /** The 403's `data` (F-161a): what opens it, for the offer's call to action. */
  denial?: AccessDenial | null
}

/** The space the route opened, as the API returned it, plus the section it lives in. */
export type SpaceData = Space & { sectionSlug?: string; section?: unknown; [key: string]: unknown }

interface RouteContextType {
  currentPath: string
  /** `replace`: the new address takes the current history entry (a redirect, so Back does not bounce). */
  navigate: (path: string, params?: Record<string, string>, options?: { replace?: boolean }) => void
  params: Record<string, string>
  spaceData: SpaceData | null
  isLoadingSpace: boolean
  /** Set only while the current route is a space the API answered 403 for (APP-55). */
  deniedSpace: DeniedSpace | null
  /**
   * True from a navigation (or a menu load) until the route effect below has
   * resolved it. In that first render `spaceData` is still the previous route's
   * (or null) and `isLoadingSpace` is still false, so without this the app drew
   * the home space or the previous module for a frame (APP-173).
   */
  isResolvingRoute: boolean
}

const isForbidden = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status === 403

const RouteContext = createContext<RouteContextType | undefined>(undefined)

export function RouteProvider({ children }: { children: React.ReactNode }) {
  /* An old Portuguese path (/oferta/, /pagamento/, …/gerenciar) is rewritten
     in the address bar before anything routes on it. */
  const [currentPath, setCurrentPath] = useState(() => canonicalizeLocation())
  const [params, setParams] = useState<Record<string, string>>({})
  const [manualParams, setManualParams] = useState<Record<string, string>>({})
  const [spaceData, setSpaceData] = useState<SpaceData | null>(null)
  const [isLoadingSpace, setIsLoadingSpace] = useState(false)
  const [deniedState, setDeniedState] = useState<DeniedSpace | null>(null)
  const { t } = useTranslation('navigation')
  const { sections } = useMenu()
  const { community } = useCommunity()
  const lastNavigateRef = React.useRef<{ path: string; time: number } | null>(null)
  /* The address shown before a back/forward, so a guarded move can be put back (navigationGuard). */
  const currentPathRef = React.useRef<string>(window.location.pathname + window.location.search + window.location.hash)
  useEffect(() => { currentPathRef.current = window.location.pathname + window.location.search + window.location.hash }, [currentPath])
  /* F-146: the canonical address follows the page. */
  useEffect(() => { syncCanonical(window.location.pathname) }, [currentPath])
  const navigateRef = React.useRef<(target: string, parameters?: Record<string, string>) => void>(() => {})

  useEffect(() => {
    window.scrollTo(0, 0)

    // Update page title based on current path
    const updatePageTitle = () => {
      /* Nothing is known about the community yet, and the title the server
         wrote is better than the product's own name. Overwriting it here is
         what made the tab flash "Memberfy" before settling. */
      if (!community) return

      /* On the route the server rendered, its title stands: it resolved the
         call, which this only knows as the space it lives in. Ownership moves
         here on the first navigation. */
      if (serverOwnsTitleFor(currentPath)) return

      /* The query is not part of the route: `/{section}/{space}/manage?tab=…`
         is still the item `manage`. */
      const pathSegments = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)

      /* The home page is the community, so its own SEO title is the right one.
         Everywhere else the suffix is the plain name: `metaTitle` is a full
         sentence ("Comunidade API Connect Conference | APIs, IA e Networking"),
         and behind a page title it produced three titles in one line. */
      let pageTitle = community.metaTitle || community.name

      // Static routes
      const staticRoutes: Record<string, string> = {
        '/content': t('content'),
        '/events': t('events'),
        '/courses': t('courses'),
        '/ranking': t('ranking'),
        '/gallery': t('gallery'),
        '/members': t('members'),
        '/logs': t('logs'),
        [ROUTES.myCompany]: t('myCompany'),
        '/engagement': t('engagement'),
        '/analytics': t('analytics'),
        '/settings': t('settings'),
        '/pricing': t('pricing'),
        '/content/details': t('contentDetails'),
        '/courses/details': t('courseDetails'),
        '/events/details': t('eventDetails')
      }

      /* Same rule the server uses, from the same module: the community's name
         is dropped when the page title already says it. Without that, a space
         inside a community named after it reads the same thing twice. */
      /* Settings has one page per topic (/settings/domain…); all of them are "Settings". */
      const titleRoute = isSettingsPath(currentPath) ? '/settings' : currentPath
      if (staticRoutes[titleRoute]) {
        pageTitle = withinCommunity(staticRoutes[titleRoute], community.name)
      } else if (pathSegments.length === 2 && spaceData) {
        // Dynamic route with space data
        pageTitle = withinCommunity(spaceData.title, community.name)
      }

      document.title = pageTitle
    }

    updatePageTitle()

    /* F-103 §6.6: answering a survey reaches no analytics, neither GA nor the community's own. */
    if (isSurveyPath(currentPath) || isPublicFormPath(currentPath)) return
    /* Every render of the route reports it; the tracker sends each path
       once, after its title settles, and holds it until GA is on. */
    trackPageView(currentPath, document.title)
    /* The community's own analytics (F-88), with the space only once it is this route's. */
    const routeSpaceSlug = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)[1]
    collectPageView(currentPath, spaceData && spaceData.slug === routeSpaceSlug ? { id: spaceData.id, module: spaceData.module } : null)
  }, [currentPath, spaceData, community, t])

  useEffect(() => {
    const handlePathChange = (event: PopStateEvent) => {
      /* An editor with unsaved changes: put the page back and ask; going on replays the move. */
      const target = window.location.pathname + window.location.search + window.location.hash
      const previous = currentPathRef.current
      /* Same address: an entry a bottom sheet pushed was taken ("back" closed it). Nothing to route,
         and resetting the params from that entry's state would drop the item (`itemId`) of the page. */
      if (previous === target) return
      if (previous && previous !== target && interceptNavigation(() => navigateRef.current(target, event.state?.params || {}))) {
        window.history.pushState(window.history.state, '', previous)
        return
      }
      console.log('⬅️ [RouteContext] Browser back/forward navigation:', window.location.pathname)
      console.log('⬅️ [RouteContext] State:', event.state)
      setCurrentPath(canonicalizeLocation())
      // Restore params from history state if available
      if (event.state?.params) {
        console.log('✅ [RouteContext] Restoring params from history:', event.state.params)
        setManualParams(event.state.params)
        setParams(event.state.params)
      } else {
        console.log('🧹 [RouteContext] No params in history, clearing')
        setManualParams({})
      }
    }
    window.addEventListener('popstate', handlePathChange)
    return () => window.removeEventListener('popstate', handlePathChange)
  }, [])

  /* The route the space below was loaded for: a rerun with the same route is a menu change (live sidebar). */
  const routeKeyRef = React.useRef<string | null>(null)
  /* What the last finished run of the route effect resolved; a newer run makes it stale. */
  const routeRunRef = React.useRef(0)
  const [resolvedRoute, setResolvedRoute] = useState<{ path: string; sections: unknown; manualParams: unknown } | null>(null)

  // Check if current path is a dynamic space route
  useEffect(() => {
    const routeKey = `${currentPath}|${JSON.stringify(manualParams)}`
    const sameRoute = routeKeyRef.current === routeKey
    routeKeyRef.current = routeKey
    const checkDynamicRoute = async () => {
      console.log('🔄 [RouteContext] useEffect disparado, currentPath:', currentPath)
      /* The query is not part of the route: `/{section}/{space}/manage?tab=…`
         is still the item `manage`. */
      const pathSegments = currentPath.split(/[?#]/)[0].split('/').filter(Boolean)
      console.log('📊 [RouteContext] pathSegments:', pathSegments)

      // Static routes should not be processed here
      const staticRoutes = ['/pricing', '/products', ROUTES.authSetPassword, ROUTES.supportEntry, ROUTES.adminFees, ROUTES.myDownloads, ROUTES.myParcels, ROUTES.myDayPasses, ROUTES.myCompany, '/checkout-success', '/settings', '/account', '/members', '/monetization', '/campaigns', '/workflows', '/dashboard', '/logs', '/engagement', '/analytics']
      // Plan links (/pricing/{plan}/{option}) are not section/space routes either.
      // The MCP consent screen (F-40) always carries its OAuth query.
      const isOAuthConsent = currentPath.split('?')[0] === ROUTES.mcpAuthorize
      /* F-115: "Eventos que organizo" and an event's door are pages of their own, not `/{section}/{space}`
         (read as one, the space-follow sent them to `/{section}/events/organizing`, an event that does not exist). */
      const isEventsOwnPage = currentPath.split(/[?#]/)[0] === ORGANIZING_EVENTS_PATH || Boolean(eventCheckInIdOf(currentPath.split(/[?#]/)[0]))
      if (staticRoutes.includes(currentPath) || isSettingsPath(currentPath) || isAdminCommunitiesPath(currentPath) || isAdminCommunityRequestsPath(currentPath) || isPricingPath(currentPath) || isOfferPath(currentPath) || isPaymentPath(currentPath) || isProductPath(currentPath) || isWaitlistPath(currentPath) || isVerifyPath(currentPath) || isInviteDeclinePath(currentPath) || isEmailUnsubscribePath(currentPath) || isAccountErasureCancelPath(currentPath) || Boolean(privacyExportIdOf(currentPath)) || currentPath.split(/[?#]/)[0] === ROUTES.accountEmails || isSurveyPath(currentPath) || isPublicFormPath(currentPath) || isSupportTicketPath(currentPath) || isBookingNewPath(currentPath.split('?')[0]) || isOAuthConsent || isEventsOwnPage) {
        setSpaceData(null)
        setParams(manualParams)
        setIsLoadingSpace(false)
        return
      }

      // Check if we have manual params with sectionSlug and spaceSlug
      // (e.g., from navigating to a detail page that preserves space context)
      if (manualParams.sectionSlug && manualParams.spaceSlug && sections.length > 0) {
        const section = sections.find(s => s.slug === manualParams.sectionSlug)
        if (section && section.spaces) {
          const space = section.spaces.find(s => s.slug === manualParams.spaceSlug)
          if (space) {
            // Only update if space data doesn't match current space
            if (!spaceData || spaceData.id !== space.id) {
              setIsLoadingSpace(true)
              setParams(manualParams)

              try {
                const fullSpace = await sdk.spaces.get(space.id)
                setDeniedState(null)
                if (fullSpace) {
                  setSpaceData({
                    ...fullSpace,
                    section: section
                  })
                } else {
                  setSpaceData(null)
                }
              } catch (error) {
                setSpaceData(null)
                setDeniedState(isForbidden(error) ? { path: currentPath, space, section, denial: denialOf(error) } : null)
              } finally {
                setIsLoadingSpace(false)
              }
            } else {
              // Space data already matches, just update params
              setParams(manualParams)
            }
            return
          }
        }
      }

      // Check if it's a potential section/space route (2, 3 or 5 segments)
      // 2: /{sectionSlug}/{spaceSlug}
      // 3: /{sectionSlug}/{spaceSlug}/{itemId}
      // 5: /{sectionSlug}/{spaceSlug}/{itemId}/lessons/{lessonId} (a course lesson)
      const isLessonRoute = pathSegments.length === 5 && pathSegments[3] === 'lessons'
      if ((pathSegments.length === 2 || pathSegments.length === 3 || isLessonRoute) && sections.length > 0) {
        const [sectionSlug, spaceSlug, itemId] = pathSegments
        console.log('🛣️ [RouteContext] Processando rota dinâmica')
        console.log('📊 [RouteContext] pathSegments:', pathSegments)
        console.log('📋 [RouteContext] sectionSlug:', sectionSlug, 'spaceSlug:', spaceSlug, 'itemId:', itemId)

        // Find section by slug
        const section = sections.find(s => s.slug === sectionSlug)
        console.log('🔎 [RouteContext] seções carregadas:', sections.map(s => s.slug).join(', '))
        if (section && section.spaces) {
          // Find space by slug within the section
          const space = section.spaces.find(s => s.slug === spaceSlug)
          console.log('🔎 [RouteContext] espaços da seção:', section.spaces.map(s => s.slug).join(', '))
          if (space && sameRoute && spaceData?.id === space.id) {
            /* Same route, the menu changed (another admin edited it): the space on screen is
               reloaded quietly, without the loading state. A refusal still shows its screen;
               a space that left the menu falls through to the not-found state below. */
            try {
              const fullSpace = await sdk.spaces.get(space.id)
              if (fullSpace) setSpaceData({ ...fullSpace, section })
            } catch (error) {
              if (isForbidden(error)) {
                setSpaceData(null)
                setDeniedState({ path: currentPath, space, section, denial: denialOf(error) })
              }
            }
            return
          }
          if (space) {
            // Set loading state immediately but keep previous data
            setIsLoadingSpace(true)

            // Build params based on number of segments
            const routeParams: Record<string, string> = {
              sectionSlug,
              spaceSlug
            }

            // For 3 segments: it's a detail page
            if (pathSegments.length === 3 || isLessonRoute) {
              routeParams.itemId = itemId
              console.log('✅ [RouteContext] Detectado itemId, setando nos params:', itemId)
            }
            if (isLessonRoute) {
              routeParams.lessonId = pathSegments[4]
            }

            console.log('📦 [RouteContext] routeParams final:', routeParams)
            setParams(routeParams)

            try {
              const fullSpace = await sdk.spaces.get(space.id)
              console.log('🌐 [RouteContext] sdk.spaces.get retornou:', fullSpace ? 'ok' : 'vazio')
              setDeniedState(null)
              if (fullSpace) {
                // Only update spaceData after successful fetch
                setSpaceData({
                  ...fullSpace,
                  section: section
                })
              } else {
                // Only clear on error
                setSpaceData(null)
              }
            } catch (caught) {
              const error = caught as { message?: string; response?: { status?: number; data?: { message?: string } } } | null
              /* A 403 is an expected answer now (a padlocked space, F-161a), not a failure. */
              if (!isForbidden(error)) console.error('❌ [RouteContext] failed to fetch space:', error?.response?.status, error?.response?.data?.message || error?.message)
              // Only clear on error
              setSpaceData(null)
              /* A 403 is an answer about access, so it gets its own screen;
                 anything else keeps falling back to the home space. */
              setDeniedState(isForbidden(error) ? { path: currentPath, space, section, denial: denialOf(error) } : null)
            } finally {
              setIsLoadingSpace(false)
            }
            return
          }
        }
      }

      /* The space is not in the section the address names: it was moved (here, in another tab,
         or an old link from a bookmark or e-mail). If the menu lists it in exactly one other
         section, the address follows it, replacing the dead entry so Back does not land on it.
         The menu holds only what this person may see (a padlocked space is listed and keeps its
         own screen); a slug in two sections is ambiguous and is not guessed. */
      if ((pathSegments.length === 2 || pathSegments.length === 3 || isLessonRoute) && sections.length > 0) {
        const [sectionSlug, spaceSlug, ...rest] = pathSegments
        /* The space on screen, found by id when only the menu changed: this also follows a slug
           the server rewrote to keep it unique in the destination section. */
        const byId = sameRoute && spaceData
          ? sections.flatMap(s => (s.spaces || []).filter(sp => sp.id === spaceData.id).map(sp => ({ sectionSlug: s.slug, spaceSlug: sp.slug })))
          : []
        const bySlug = sections
          .filter(s => s.slug !== sectionSlug && s.spaces?.some(sp => sp.slug === spaceSlug))
          .map(s => ({ sectionSlug: s.slug, spaceSlug }))
        const holders = byId.length === 1 ? byId : bySlug
        if (holders.length === 1) {
          const target = holders[0]
          const suffix = currentPath.match(/[?#].*$/)?.[0] ?? window.location.search + window.location.hash
          const movedPath = `/${[target.sectionSlug, target.spaceSlug, ...rest].join('/')}${suffix}`
          const movedParams = manualParams.sectionSlug === sectionSlug
            ? { ...manualParams, sectionSlug: target.sectionSlug, ...(manualParams.spaceSlug ? { spaceSlug: target.spaceSlug } : {}) }
            : manualParams
          window.history.replaceState({ ...(window.history.state || {}), params: movedParams }, '', movedPath)
          setManualParams(movedParams)
          setCurrentPath(movedPath)
          return
        }
      }

      // For non-dynamic routes, clear space data
      // But preserve manual params if they exist
      setSpaceData(null)
      if (Object.keys(manualParams).length === 0) {
        setParams({})
      } else {
        setParams(manualParams)
      }
      setIsLoadingSpace(false)
    }

    const run = ++routeRunRef.current
    void checkDynamicRoute().finally(() => {
      if (run === routeRunRef.current) setResolvedRoute({ path: currentPath, sections, manualParams })
    })
  }, [currentPath, sections, manualParams])
  const isResolvingRoute = !resolvedRoute || resolvedRoute.path !== currentPath || resolvedRoute.sections !== sections || resolvedRoute.manualParams !== manualParams

  const navigate = (target: string, parameters: Record<string, string> = {}, options: { replace?: boolean } = {}) => {
    /* A page-level editor with unsaved changes asks first (navigationGuard). */
    if (interceptNavigation(() => navigate(target, parameters, options))) return
    const path = canonicalPath(target)
    // Prevent duplicate navigation calls within 100ms to the same path
    const now = Date.now()
    if (
      lastNavigateRef.current &&
      lastNavigateRef.current.path === path &&
      now - lastNavigateRef.current.time < 100
    ) {
      return
    }

    lastNavigateRef.current = { path, time: now }

    // Save params in history state so we can restore them on back/forward
    if (options.replace) window.history.replaceState({ params: parameters }, '', path)
    else window.history.pushState({ params: parameters }, '', path)

    // Clear manual params if navigating to a different route without new params
    if (path !== currentPath) {
      setManualParams(parameters)
    } else {
      setManualParams(prev => ({ ...prev, ...parameters }))
    }

    setCurrentPath(path)
    setParams(parameters)
  }

  navigateRef.current = navigate

  /* A plain <a href> to the app's own host (rich text, server HTML, an
     e-mail-style link) opens in place, like an AppLink, instead of reloading
     the app. Listening on the document runs after React's own handlers, so an
     AppLink or a button that already handled the click is left alone. */
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const url = internalLinkTarget(event)
      if (!url) return
      event.preventDefault()
      navigateRef.current(url.pathname + url.search)
      /* Only when the move happened: an editor with unsaved changes may have held it. */
      if (url.hash && window.location.pathname + window.location.search === url.pathname + url.search) {
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
        scrollToHash(url.hash)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  /* Matched against the path so a refusal never outlives its route: between
     a navigation and the fetch that follows it, the old state is ignored. */
  const deniedSpace = deniedState && deniedState.path === currentPath ? deniedState : null

  return (
    <RouteContext.Provider value={{ currentPath, navigate, params, spaceData, isLoadingSpace, deniedSpace, isResolvingRoute }}>
      {children}
      <NavigationGuardPrompt />
    </RouteContext.Provider>
  )
}

export function useRoute() {
  const context = useContext(RouteContext)
  if (!context) {
    throw new Error('useRoute must be used within a RouteProvider')
  }
  return context
}